"use client";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useDataFetch } from "../hooks/use-data-fetch";
import { useUserStore } from "../store/user-store";
import { User } from "../store/user-store";

// Definir la estructura del contexto
interface UserContextType {
  isLoading: boolean;
  error: Error | null;
  refetchUserData: () => Promise<void>;
  userData: User | null;
}

// Crear el contexto
const UserContext = createContext<UserContextType | undefined>(undefined);

// Crear el hook personalizado para usar el contexto
export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser debe ser usado dentro de un UserProvider");
  }
  return context;
};

// Props para el provider
interface UserProviderProps {
  children: ReactNode;
}

// Crear el provider
export const UserProvider = ({ children }: UserProviderProps) => {
  const { getData } = useDataFetch();
  const setUser = useUserStore(state => state.setUser);
  const userData = useUserStore(state => state.user);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Usar useQuery directamente en el componente
  const { data, refetch, isLoading: queryIsLoading, error: queryError } = getData(["user-profile-init2"], "/users");
  
  // Función para refetch expuesta al contexto
  const refetchUserData = async () => {
    try {
      setIsLoading(true);
      const result = await refetch();
      
      if (result.data?.data?.user) {
        const { user, parent, country } = result.data.data;
        setUser({ ...user, parent, country });
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setIsLoading(false);
    }
  };

  // Efecto para actualizar el usuario cuando cambian los datos
  useEffect(() => {
    if (data?.data?.user) {
      const { user, parent, country } = data.data;
      setUser({ ...user, parent, country });
    }
  }, [data, setUser]);

  // Valor del contexto
  const value = {
    isLoading: isLoading || queryIsLoading,
    error: error || (queryError as Error | null),
    refetchUserData,
    userData
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
